import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Images
import drums from "../images/gallery/061A9166.webp";
import grandPiano from "../images/gallery/class2.webp";
import keyboardRoom from "../images/gallery/class3.webp";
import drumStudio from "../images/gallery/drumsClass.webp";
import entrance from "../images/gallery/entrance.webp";
import guitarRoom from "../images/gallery/guitarRoom.webp";
import guitars from "../images/gallery/guitars.webp";
import pianoClass from "../images/gallery/pionoclass.webp";

const sections = [
  {
    image: entrance,
    heading: "Step Into the Space",
    subheading: "Where it all begins.",
    text: "A calm, welcoming lounge and reception that sets the tone before the first note.",
  },
  {
    image: guitarRoom,
    heading: "Guitar Practice Room",
    subheading: "for Future Rockstars.",
    text: "Dedicated acoustic spaces for individual expression and practice.",
  },
  {
    image: guitars,
    heading: "Wall of Guitars",
    subheading: "for Every Style.",
    text: "From classical to electric — choose your sound, shape your voice.",
    isPortrait: true,
  },
  {
    image: pianoClass,
    heading: "Piano Excellence Studio",
    subheading: "for Classical Brilliance.",
    text: "Experience classical training in acoustically balanced rooms.",
  },
  {
    image: grandPiano,
    heading: "The Grand Piano Hall",
    subheading: "for Moments of Mastery.",
    text: "Perform or practice in our elegant grand piano hall — a space crafted for resonance, focus, and expression.",
  },
  {
    image: keyboardRoom,
    heading: "Keyboard Practice Suite",
    subheading: "for Every Beginner.",
    text: "Private, soundproofed booths where new learners build technique at their own pace.",
  },
  {
    image: drums,
    heading: "Drumming Foundations",
    subheading: "for Skillful Hands.",
    text: "Kickstart your rhythmic journey with hands-on, one-on-one drumming lessons.",
  },
  {
    image: drumStudio,
    heading: "Innovative Learning Spaces",
    subheading: "for Creative Minds.",
    text: "Explore our cutting-edge drum studios where rhythm meets precision.",
  },
];

export default function GallerySplitScroll() {
  return (
    <div className="bg-white">
      {/* Page header */}
      <section className="px-6 pb-4 pt-20 text-center sm:pt-24">
        <motion.span
          className="eyebrow mb-5"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Step Inside
        </motion.span>
        <motion.h1
          className="section-heading"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          A Look Around Our Studio
        </motion.h1>
        <motion.p
          className="mx-auto mt-5 max-w-2xl text-ink-soft"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Purpose-built rooms, professional instruments, and spaces designed to
          help every student find their sound.
        </motion.p>
      </section>

      {sections.map((section, index) => (
        <SplitSection key={index} {...section} reverse={index % 2 !== 0} />
      ))}
    </div>
  );
}

function SplitSection({ image, heading, subheading, text, reverse, isPortrait }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const yImage = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const yHeading = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const ySub = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section
      ref={ref}
      className={`flex min-h-[85vh] flex-col items-center md:flex-row ${
        reverse ? "md:flex-row-reverse" : ""
      } gap-y-10 px-4 py-10 sm:px-6 md:gap-x-20 md:py-16 lg:px-20`}
    >
      {/* IMAGE BLOCK */}
      <motion.div
        style={{ y: yImage }}
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`w-full md:w-1/2 ${
          isPortrait ? "h-[85vh]" : "h-[62vh]"
        } mb-6 flex items-center justify-center md:mb-0`}
      >
        <img
          src={image}
          alt={heading || "Gallery image"}
          className={`rounded-3xl shadow-card ${
            isPortrait
              ? "h-full max-h-full w-auto object-contain"
              : "h-full w-full object-cover"
          }`}
          loading="lazy"
        />
      </motion.div>

      {/* TEXT BLOCK */}
      <motion.div
        className="w-full text-center md:w-1/2 md:text-left"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <motion.h2
          style={{ y: yHeading }}
          className="relative mb-3 inline-block text-3xl font-extrabold leading-tight text-ink sm:text-4xl md:text-5xl"
        >
          <span className="relative z-10">{heading}</span>
          <span className="absolute -bottom-1 left-0 z-0 h-2 w-full rounded-md bg-gray-100"></span>
        </motion.h2>

        <motion.h3
          style={{ y: yHeading }}
          className="mb-5 text-2xl font-semibold text-brand-500 sm:text-3xl"
        >
          {subheading}
        </motion.h3>

        <motion.p
          style={{ y: ySub }}
          className="mx-auto max-w-xl text-base text-ink-soft sm:text-lg md:mx-0"
        >
          {text}
        </motion.p>
      </motion.div>
    </section>
  );
}
